import { useMutation } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  Clock,
  Flame,
  Lightbulb,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useOutlets } from '@/hooks/useOutlets';
import type { OutletBreakdown } from '@/hooks/useOutletProviderBreakdown';
import { cn } from '@/lib/utils';

interface Props {
  outlets: OutletBreakdown[];
  period: string;
}

type Priority = 'urgent' | 'high' | 'medium';
type Category = 'quality' | 'growth' | 'ops' | 'ads' | 'balance';

interface InsightAction {
  priority: Priority;
  category: Category;
  outlet: string;
  platform: 'Swiggy' | 'Zomato' | 'Both';
  title: string;
  evidence: string;
  action: string;
}

interface SummaryResponse {
  headline?: string;
  actions?: InsightAction[];
  fallback?: string;
  cached?: boolean;
  generatedAt?: string;
}

async function fetchSummary(payload: unknown, force = false): Promise<SummaryResponse> {
  const res = await fetch(`/api/insights/aggregator-gap${force ? '?force=1' : ''}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`${res.status}: ${detail.slice(0, 200)}`);
  }
  return (await res.json()) as SummaryResponse;
}

function ago(iso?: string): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const PRIORITY_STYLE: Record<Priority, { badge: string; icon: typeof Flame; border: string }> = {
  urgent: {
    badge: 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30',
    icon: Flame,
    border: 'border-l-rose-500',
  },
  high: {
    badge: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
    icon: AlertTriangle,
    border: 'border-l-amber-500',
  },
  medium: {
    badge: 'bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30',
    icon: Lightbulb,
    border: 'border-l-sky-500',
  },
};

const CATEGORY_LABEL: Record<Category, string> = {
  quality: 'Quality',
  growth: 'Growth',
  ops: 'Ops',
  ads: 'Ads',
  balance: 'Platform mix',
};

const PLATFORM_DOT: Record<'Swiggy' | 'Zomato' | 'Both', string> = {
  Swiggy: 'bg-orange-500',
  Zomato: 'bg-rose-500',
  Both: 'bg-violet-500',
};

export function AISummary({ outlets, period }: Props) {
  const { data: outletList = [] } = useOutlets();
  const nameOf = (id: string) => outletList.find((o) => o.id === id)?.name ?? id;

  const mutation = useMutation({
    mutationFn: (force: boolean) =>
      fetchSummary(
        {
          period,
          outlets: outlets.map((o) => ({
            outletId: o.outletId,
            outletName: nameOf(o.outletId),
            swiggy: o.swiggy,
            zomato: o.zomato,
          })),
        },
        force,
      ),
  });

  const actions = mutation.data?.actions ?? [];

  return (
    <div className="rounded-lg border border-border/60 bg-gradient-to-br from-violet-500/5 to-sky-500/5 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Sparkles className="h-4 w-4 text-violet-500" />
          AI insight — where's the gap?
          {mutation.data?.generatedAt && (
            <span className="flex items-center gap-1 text-2xs font-normal text-muted-foreground">
              <Clock className="h-3 w-3" />
              {mutation.data.cached ? 'Cached · ' : ''}
              {ago(mutation.data.generatedAt)}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => mutation.mutate(false)}
            disabled={mutation.isPending || outlets.length === 0}
            className="h-7 text-2xs"
          >
            {mutation.isPending ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin" />
                Analyzing…
              </>
            ) : mutation.data ? (
              <>
                <RefreshCw className="h-3 w-3" />
                Reload
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3" />
                Generate
              </>
            )}
          </Button>
          {mutation.data && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => mutation.mutate(true)}
              disabled={mutation.isPending}
              className="h-7 text-2xs"
              title="Bypass the 7-day cache and call Claude fresh"
            >
              Force refresh
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 text-sm">
        {mutation.isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : mutation.isError ? (
          <div className="flex items-start gap-2 text-rose-600 dark:text-rose-400">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="text-xs">
              Could not generate summary. {(mutation.error as Error).message}
            </span>
          </div>
        ) : mutation.data ? (
          mutation.data.fallback ? (
            <p className="whitespace-pre-wrap text-foreground/90">{mutation.data.fallback}</p>
          ) : (
            <div className="space-y-3">
              {mutation.data.headline && (
                <p className="font-medium text-foreground/90">{mutation.data.headline}</p>
              )}
              {actions.length > 0 ? (
                <div className="grid gap-2">
                  {actions.map((a, i) => (
                    <ActionCard key={i} action={a} />
                  ))}
                </div>
              ) : (
                <p className="text-2xs text-muted-foreground">
                  No actions returned. Try Force refresh.
                </p>
              )}
            </div>
          )
        ) : (
          <p className="text-muted-foreground">
            Click <span className="font-medium">Generate</span> to have Claude analyze the gap
            and return a prioritized list of actions.
          </p>
        )}
      </div>
    </div>
  );
}

function ActionCard({ action }: { action: InsightAction }) {
  const style = PRIORITY_STYLE[action.priority] ?? PRIORITY_STYLE.medium;
  const Icon = style.icon;
  return (
    <div
      className={cn(
        'rounded-md border border-border/40 bg-background/70 p-3 border-l-4',
        style.border,
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="muted" className={cn('text-2xs', style.badge)}>
          <Icon className="mr-1 h-3 w-3" />
          {action.priority}
        </Badge>
        <Badge variant="muted" className="text-2xs">
          {CATEGORY_LABEL[action.category] ?? action.category}
        </Badge>
        <span className="flex items-center gap-1 text-2xs text-muted-foreground">
          <span className={cn('h-1.5 w-1.5 rounded-full', PLATFORM_DOT[action.platform])} />
          {action.platform} · {action.outlet}
        </span>
      </div>
      <div className="mt-2 font-medium leading-tight">{action.title}</div>
      <div className="mt-1.5 text-xs text-muted-foreground">
        <span className="font-medium text-foreground/70">Evidence:</span> {action.evidence}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        <span className="font-medium text-foreground/70">Do this:</span> {action.action}
      </div>
    </div>
  );
}
